/**
 * Design System Preview Page — HMVMReviewer Phase 1
 * 
 * Interactive showcase of all design system components and theme variants.
 * Demonstrates:
 * - All component states and variants
 * - Theme switching (Electric Blue / Neutral accents)
 * - Dark/Light base theme support
 * - Semantic colors independence from product accent
 */

import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  Button,
  Input,
  Select,
  Textarea,
  Badge,
  StatusBadge,
  SeverityBadge,
  Card,
  Panel,
  Modal,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  LoadingSpinner,
  Skeleton,
  EmptyState,
  ErrorState,
  KnowledgeTag,
  CodeBlock,
  Diff,
  ScoreBadge,
  ScoreCard,
} from '../components/design-system';

export function DesignSystemPreviewPage() {
  const { themeBase, setThemeBase, themeAccent, setThemeAccent, isDark } = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [selectValue, setSelectValue] = useState('');

  return (
    <div className="min-h-screen bg-[var(--reviewer-bg)] py-8">
      <div className="container-wide">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-3xl)] font-semibold font-mono mb-2">
            HMVMReviewer Design System — Phase 1
          </h1>
          <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-base)] font-mono">
            Interactive component library showcase with theme switching
          </p>
        </div>

        {/* Theme Controls */}
        <Panel
          title="Theme Controls"
          className="mb-8"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-md)] font-medium font-mono mb-3">
                Base Theme (Dark/Light)
              </h4>
              <div className="flex gap-2">
                <Button
                  variant={themeBase === 'dark' ? 'primary' : 'secondary'}
                  onClick={() => setThemeBase('dark')}
                >
                  Dark
                </Button>
                <Button
                  variant={themeBase === 'light' ? 'primary' : 'secondary'}
                  onClick={() => setThemeBase('light')}
                >
                  Light
                </Button>
                <Button
                  variant={themeBase === 'system' ? 'primary' : 'secondary'}
                  onClick={() => setThemeBase('system')}
                >
                  System
                </Button>
              </div>
              <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono mt-2">
                Current: {themeBase} (resolved: {isDark ? 'dark' : 'light'})
              </p>
            </div>

            <div>
              <h4 className="text-[var(--reviewer-text)] text-[var(--reviewer-text-md)] font-medium font-mono mb-3">
                Product Accent Theme
              </h4>
              <div className="flex gap-2">
                <Button
                  variant={themeAccent === 'reviewer-electric-blue' ? 'primary' : 'secondary'}
                  onClick={() => setThemeAccent('reviewer-electric-blue')}
                >
                  Electric Blue
                </Button>
                <Button
                  variant={themeAccent === 'reviewer-neutral' ? 'primary' : 'secondary'}
                  onClick={() => setThemeAccent('reviewer-neutral')}
                >
                  Neutral
                </Button>
              </div>
              <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono mt-2">
                Current: {themeAccent}
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[var(--reviewer-accent-soft)] border border-[var(--reviewer-accent-border)]">
            <p className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-mono">
              <strong>Test:</strong> Switch accent themes above. Notice how the PRIMARY button, focus rings, and Knowledge tags change color, 
              but SUCCESS (green), WARNING (yellow), and DANGER (red) badges below stay the same. 
              Semantic colors are independent of product accent!
            </p>
          </div>
        </Panel>

        {/* Buttons */}
        <Panel title="Buttons" className="mb-8">
          <div className="space-y-6">
            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Variants
              </h5>
              <div className="flex flex-wrap gap-2">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="ghost">Ghost</Button>
              </div>
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Sizes
              </h5>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
              </div>
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                States
              </h5>
              <div className="flex flex-wrap gap-2">
                <Button disabled>Disabled</Button>
                <Button loading>Loading</Button>
                <Button variant="primary" fullWidth>Full Width</Button>
              </div>
            </div>
          </div>
        </Panel>

        {/* Form Inputs */}
        <Panel title="Form Inputs" className="mb-8">
          <div className="space-y-6">
            <Input
              label="Text Input"
              placeholder="Enter text..."
              helperText="This is helper text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              fullWidth
            />

            <Input
              label="Required Input"
              placeholder="This field is required"
              required
              fullWidth
            />

            <Input
              label="Input with Error"
              placeholder="Enter valid email"
              type="email"
              error="Please enter a valid email address"
              fullWidth
            />

            <Select
              label="Select Dropdown"
              options={[
                { value: 'option1', label: 'Option 1' },
                { value: 'option2', label: 'Option 2' },
                { value: 'option3', label: 'Option 3' },
              ]}
              placeholder="Choose an option"
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
              fullWidth
            />

            <Textarea
              label="Textarea"
              placeholder="Enter description..."
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              rows={4}
              showCharCount
              maxLength={200}
              fullWidth
            />
          </div>
        </Panel>

        {/* Badges - Demonstrating Semantic Color Independence */}
        <Panel title="Badges — Semantic Colors (Independent of Accent)" className="mb-8">
          <div className="space-y-6">
            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Generic Badges
              </h5>
              <div className="flex flex-wrap gap-2">
                <Badge variant="default">Default</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="danger">Danger</Badge>
                <Badge variant="info">Info</Badge>
                <Badge variant="accent">Accent (Changes)</Badge>
              </div>
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Status Badges (Semantic)
              </h5>
              <div className="flex flex-wrap gap-2">
                <StatusBadge status="completed" />
                <StatusBadge status="running" />
                <StatusBadge status="pending" />
                <StatusBadge status="failed" />
                <StatusBadge status="resolved" />
                <StatusBadge status="open" />
              </div>
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Severity Badges (Semantic - Always Same Colors)
              </h5>
              <div className="flex flex-wrap gap-2">
                <SeverityBadge severity="critical" />
                <SeverityBadge severity="high" />
                <SeverityBadge severity="medium" />
                <SeverityBadge severity="low" />
                <SeverityBadge severity="info" />
              </div>
            </div>
          </div>
        </Panel>

        {/* Cards & Panels */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card>
            <h4 className="text-[var(--reviewer-text)] font-mono mb-2">Simple Card</h4>
            <p className="text-[var(--reviewer-text-secondary)] text-sm font-mono">
              This is a basic card component with default styling.
            </p>
          </Card>

          <Card variant="elevated" interactive>
            <h4 className="text-[var(--reviewer-text)] font-mono mb-2">Interactive Card</h4>
            <p className="text-[var(--reviewer-text-secondary)] text-sm font-mono">
              This card has hover effects and uses elevated variant.
            </p>
          </Card>
        </div>

        {/* Table */}
        <Panel title="Table" className="mb-8">
          <Table striped hoverable>
            <TableHead>
              <TableRow>
                <TableHeader>ID</TableHeader>
                <TableHeader>Finding</TableHeader>
                <TableHeader>Severity</TableHeader>
                <TableHeader>Status</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody striped hoverable>
              <TableRow>
                <TableCell>F001</TableCell>
                <TableCell>SQL Injection Vulnerability</TableCell>
                <TableCell><SeverityBadge severity="critical" size="sm" /></TableCell>
                <TableCell><StatusBadge status="open" size="sm" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>F002</TableCell>
                <TableCell>Missing Input Validation</TableCell>
                <TableCell><SeverityBadge severity="high" size="sm" /></TableCell>
                <TableCell><StatusBadge status="investigating" size="sm" /></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>F003</TableCell>
                <TableCell>Deprecated API Usage</TableCell>
                <TableCell><SeverityBadge severity="medium" size="sm" /></TableCell>
                <TableCell><StatusBadge status="resolved" size="sm" /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Panel>

        {/* Loading & Skeleton */}
        <Panel title="Loading States" className="mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Loading Spinner
              </h5>
              <LoadingSpinner size="md" text="Loading..." />
            </div>
            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Skeleton Loaders
              </h5>
              <Skeleton variant="text" count={3} />
            </div>
          </div>
        </Panel>

        {/* Empty & Error States */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card>
            <EmptyState
              title="No Data Found"
              description="There are no items to display at the moment."
              action={{
                label: 'Create New',
                onClick: () => alert('Create action'),
              }}
            />
          </Card>

          <Card>
            <ErrorState
              title="Failed to Load"
              error="Network connection error"
              onRetry={() => alert('Retry action')}
            />
          </Card>
        </div>

        {/* Reviewer-Specific Components */}
        <Panel title="Reviewer-Specific Components" className="mb-8">
          <div className="space-y-6">
            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Knowledge Tags (Uses Accent — Changes with Theme)
              </h5>
              <div className="flex flex-wrap gap-2">
                <KnowledgeTag knowledgeId="K001" title="Security Best Practices" />
                <KnowledgeTag knowledgeId="K042" title="SQL Injection Prevention" clickable />
                <KnowledgeTag knowledgeId="K100" title="Code Review Guidelines" />
              </div>
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Code Block
              </h5>
              <CodeBlock
                filename="example.ts"
                language="typescript"
                code={`function validateInput(input: string): boolean {
  if (!input || input.length === 0) {
    return false;
  }
  return true;
}`}
                lineNumbers
              />
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Diff (Semantic Colors — Independent)
              </h5>
              <Diff
                filename="auth.ts"
                lines={[
                  { type: 'unchanged', lineNumber: 1, content: 'function authenticate(user: User) {' },
                  { type: 'removed', lineNumber: 2, content: '  return user.password === hash;' },
                  { type: 'added', lineNumber: 2, content: '  return bcrypt.compare(user.password, hash);' },
                  { type: 'unchanged', lineNumber: 3, content: '}' },
                ]}
              />
            </div>

            <div>
              <h5 className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono uppercase mb-3">
                Score Components
              </h5>
              <div className="flex flex-wrap gap-4 mb-4">
                <ScoreBadge score={95} maxScore={100} showLabel />
                <ScoreBadge score={72} maxScore={100} showLabel />
                <ScoreBadge score={45} maxScore={100} showLabel />
              </div>
              <ScoreCard
                title="Code Quality Score"
                score={85}
                maxScore={100}
                description="Overall code quality assessment"
                details={[
                  { label: 'Security', value: 92 },
                  { label: 'Performance', value: 78 },
                  { label: 'Maintainability', value: 85 },
                  { label: 'Test Coverage', value: 88 },
                ]}
              />
            </div>
          </div>
        </Panel>

        {/* Modal Demo */}
        <Panel title="Modal" className="mb-8">
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            Open Modal
          </Button>
          <p className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono mt-2">
            Note: Modal does NOT close when clicking backdrop (HMVM rule)
          </p>
        </Panel>

        <Modal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Example Modal"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setModalOpen(false)}>
                Confirm
              </Button>
            </div>
          }
        >
          <p className="text-[var(--reviewer-text)] font-mono mb-4">
            This modal demonstrates the HMVM rule: it does NOT close when you click the backdrop.
          </p>
          <p className="text-[var(--reviewer-text-secondary)] font-mono text-sm">
            Close via: Close button (✕), Cancel button, or ESC key.
          </p>
        </Modal>

        {/* Theme Info */}
        <Panel title="Theme Architecture" className="mb-8">
          <div className="space-y-4 text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-sm)] font-mono">
            <p>
              <strong className="text-[var(--reviewer-text)]">Default Accent:</strong> Electric Blue (#4a9eff)
            </p>
            <p>
              <strong className="text-[var(--reviewer-text)]">Architecture:</strong> Components consume semantic tokens 
              (--reviewer-accent, --reviewer-success, etc.) not hardcoded colors.
            </p>
            <p>
              <strong className="text-[var(--reviewer-text)]">Theme Persistence:</strong> Preferences saved to localStorage 
              and survive page reload.
            </p>
            <p>
              <strong className="text-[var(--reviewer-text)]">Semantic Independence:</strong> Success (green), Warning (yellow), 
              Danger (red) colors never change with product accent.
            </p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
